import { ComponentType, useCallback } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionProps,
  AccordionSummary,
  Box,
  BoxProps,
  ListItem,
  ListItemProps,
  ListItemSecondaryAction,
  ListItemSecondaryActionProps,
  ListItemText,
  ListItemTextProps,
} from '@mui/material';

export interface SecondaryActionProps<T> {
  onChange: () => void;
  value: T;
}

export interface FormOptionListItemProps<T> extends Omit<BoxProps, 'onChange'> {
  AccordionProps?: Omit<AccordionProps, 'expanded'>;
  isSelected: boolean;
  ListItemTextProps?: ListItemTextProps;
  ListItemProps?: Omit<ListItemProps, 'onClick'>;
  ListItemSecondaryActionProps?: ListItemSecondaryActionProps;
  onChange: (value: T) => void;
  SecondaryAction: ComponentType<SecondaryActionProps<T>>;
  value: T;
}

/**
 * Generic list item to be placed in a CreateOptionList, should not be rendered directly and should instead be rendered
 * via CreateOptionCheckboxListItem or CreateOptionRadioListItem.
 */
export const FormOptionListItem = <T,>({
  AccordionProps,
  children,
  isSelected,
  ListItemProps,
  ListItemTextProps,
  ListItemSecondaryActionProps,
  onChange,
  SecondaryAction,
  sx,
  value,
  ...rest
}: FormOptionListItemProps<T>) => {
  const handleOnClick = useCallback(() => onChange(value), [onChange, value]);

  const { sx: ListItemPropsSx, ...ListItemPropsRest } = ListItemProps || {};

  return (
    <Box
      sx={{
        borderBottom: ({ palette }) => `1px solid ${palette.neutrals.platinum}`,
        borderTop: ({ palette }) => `0px solid ${palette.neutrals.platinum}`,
        '&:last-of-type': {
          borderBottomWidth: {
            md: 0,
          },
        },
        '&:first-of-type': {
          borderTopWidth: {
            xs: '1px',
            md: 0,
          },
        },
        ...sx,
      }}
      {...rest}
    >
      <ListItem
        sx={{
          backgroundColor: ({ palette }) => (isSelected ? palette.neutrals.milk : undefined),
          py: 3,
          pr: { xs: 9, md: 9.5 },
          pl: { xs: 4, md: 4.5 },
          ...ListItemPropsSx,
        }}
        onClick={handleOnClick}
        {...ListItemPropsRest}
      >
        <ListItemText
          primaryTypographyProps={{ variant: 'body1.semibold', mb: 0.5 }}
          secondaryTypographyProps={{ variant: 'body1' }}
          {...ListItemTextProps}
        />
        <ListItemSecondaryAction
          sx={{ right: ({ spacing }) => ({ xs: spacing(3), md: spacing(3.5) }) }}
          {...ListItemSecondaryActionProps}
        >
          <SecondaryAction onChange={handleOnClick} value={value} />
        </ListItemSecondaryAction>
      </ListItem>
      {children && (
        <Accordion TransitionProps={{ unmountOnExit: false }} expanded={isSelected} {...AccordionProps}>
          <AccordionSummary sx={{ display: 'none' }} expandIcon={undefined} />
          <AccordionDetails
            sx={{
              backgroundColor: ({ palette }) => (isSelected ? palette.neutrals.milk : undefined),
              pt: 0,
              px: 4.5,
              pb: 4.5,
            }}
          >
            {children}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );
};
